import { Link } from '@tanstack/react-router';
import { Activity, CircleDot, MapPin, Pause, Phone, TriangleAlert, WifiOff, type LucideIcon } from 'lucide-react';
import { cn } from '@/lib/cn';
import { countryName } from '@/i18n';
import { formatAgo, formatPlate, formatWhen } from '@/lib/format';
import type { BoardRow, MotionState } from '@/api/types';

/**
 * ─────────────────────────────────────────────────────────────────────────
 *  ЕДИН БУС — ЕДНА КАРТА.
 *
 *  Вместо ред от девет колони: номерът и състоянието отгоре, шофьорът с
 *  телефона, после с какво е зает и къде отива, и накрая къде е и кога се
 *  освобождава. Цветната ивица отляво казва състоянието без да се чете:
 *  зелено — кара, жълто — стои, сиво — свободен, червено — няма връзка.
 * ─────────────────────────────────────────────────────────────────────────
 */

const MOTION: Record<MotionState, { label: string; icon: LucideIcon; text: string; bar: string }> = {
  moving: { label: 'Кара', icon: Activity, text: 'text-accent', bar: 'bg-accent' },
  idle: { label: 'Стои с работещ двигател', icon: Pause, text: 'text-warn', bar: 'bg-warn' },
  stopped: { label: 'Спрял', icon: CircleDot, text: 'text-ink-dim', bar: 'bg-ink-faint' },
  offline: { label: 'Няма връзка', icon: WifiOff, text: 'text-danger', bar: 'bg-danger' },
};

export function VanCard({ row, signals }: { row: BoardRow; signals?: { total: number; high: number } }) {
  const motion = MOTION[(row.position?.motionState ?? 'offline') as MotionState] ?? MOTION.offline;
  const MotionIcon = motion.icon;
  const stop = row.nextStop;
  /** „Ще закъснее" само когато има изчислено пристигане — иначе няма с какво да се сравни. */
  const late = stop?.etaAt != null && stop.etaSlackMin != null && stop.etaSlackMin > 0;
  const busy = row.currentOrder !== null;
  const serious = row.alerts.filter((a) => !/Няма нито един запис/.test(a));

  return (
    <article className="group relative flex flex-col overflow-hidden rounded-xl border border-line bg-surface transition-colors hover:border-accent/60">
      {/*
        Цялата карта води към буса — курсът, заявките, горивото, заплатата,
        документите. Връзката е отделен слой отдолу, а номерът на заявката и
        телефонът стоят над него, за да остане всяка от тях своя връзка.
      */}
      <Link
        to="/vehicles/$vehicleId"
        params={{ vehicleId: row.vehicleId }}
        aria-label={`Бус ${row.fleetNo} — всичко за курса`}
        className="absolute inset-0 z-0 rounded-xl focus-visible:outline focus-visible:outline-2 focus-visible:outline-accent"
      />
      <span className={cn('absolute inset-y-0 left-0 w-1', busy ? motion.bar : row.status === 'active' ? 'bg-line-strong' : 'bg-danger')} aria-hidden="true" />

      <header className="flex items-start gap-3 px-4 pb-2 pt-3">
        <div className="flex size-11 shrink-0 items-center justify-center rounded-lg bg-raised font-display text-lg font-bold">
          {row.fleetNo}
        </div>
        <div className="min-w-0 flex-1">
          <div className="font-mono text-sm tracking-wide">{formatPlate(row.plate)}</div>
          <div className={cn('mt-0.5 inline-flex items-center gap-1.5 text-xs', motion.text)}>
            <MotionIcon className="size-3.5" />
            {motion.label}
            {row.position?.speedKph ? <span className="font-mono">· {row.position.speedKph} км/ч</span> : null}
          </div>
        </div>
        {signals && signals.total > 0 ? (
          <span
            title={`${signals.total} сигнала за буса${signals.high ? `, ${signals.high} важни` : ''} — подробно на страницата му`}
            className={cn(
              'inline-flex items-center gap-0.5 rounded-full px-1.5 py-0.5 text-[11px] font-semibold',
              signals.high ? 'bg-danger-soft text-danger' : 'bg-warn-soft text-warn',
            )}
          >
            <TriangleAlert className="size-3" />
            {signals.total}
          </span>
        ) : null}
        {busy ? (
          <span className="rounded-full bg-accent-soft px-2 py-0.5 text-[11px] font-medium text-accent">на курс</span>
        ) : row.status === 'active' ? (
          <span className="rounded-full bg-raised px-2 py-0.5 text-[11px] text-ink-dim">свободен</span>
        ) : (
          <span className="rounded-full bg-danger-soft px-2 py-0.5 text-[11px] text-danger">в сервиз</span>
        )}
      </header>

      <div className="flex items-center gap-2 px-4 pb-3 text-sm">
        {row.driver ? (
          <>
            <span className="truncate">{row.driver.name}</span>
            {row.driver.phone ? (
              <a href={`tel:${row.driver.phone}`} className="relative z-10 ml-auto inline-flex items-center gap-1 text-xs text-ink-dim hover:text-accent">
                <Phone className="size-3.5" />
                <span className="font-mono">{row.driver.phone}</span>
              </a>
            ) : null}
          </>
        ) : (
          <span className="text-ink-faint">без шофьор</span>
        )}
      </div>

      <div className="mx-4 border-t border-line" />

      <div className="flex-1 px-4 py-3 text-sm">
        {row.currentOrder ? (
          <>
            <div className="flex items-baseline gap-2">
              <Link to="/orders/$orderId" params={{ orderId: row.currentOrder.id }} className="relative z-10 font-mono text-accent hover:underline">
                {row.currentOrder.number}
              </Link>
              <span className="truncate text-xs text-ink-dim">{row.currentOrder.partnerName}</span>
            </div>
            {stop ? (
              <div className="mt-1.5">
                <div className="truncate">
                  {stop.kind === 'pickup' ? 'Товари в ' : 'Разтоварва в '}
                  <b className="font-medium">{stop.city}</b> <span className="text-ink-faint">({stop.country})</span>
                </div>
                <div className={cn('text-xs', late ? 'font-medium text-danger' : 'text-ink-dim')}>
                  {stop.etaAt ? `пристига ${formatWhen(stop.etaAt)}` : 'без изчислено пристигане'}
                  {late ? ' — ще закъснее' : ''}
                  {stop.isHardDeadline ? ' · твърд срок' : ''}
                </div>
              </div>
            ) : null}
          </>
        ) : (
          <div className="text-ink-dim">
            Свободен{row.freeAtPlace ? <> · <span className="text-ink">{row.freeAtPlace}</span></> : null}
            {row.nextCommitment ? (
              <div className="mt-1 text-xs">
                следващ курс {row.nextCommitment.number}
                {row.nextCommitment.plannedStartAt ? ` · ${formatWhen(row.nextCommitment.plannedStartAt)}` : ''}
              </div>
            ) : null}
          </div>
        )}
      </div>

      <footer className="flex items-center gap-2 border-t border-line bg-raised/40 px-4 py-2 text-xs text-ink-dim">
        <MapPin className="size-3.5 shrink-0" />
        <span className="truncate">
          {row.position?.nearestPlace ?? (row.position?.country ? countryName(row.position.country) : 'няма позиция')}
          {row.position?.lastSeenAt ? ` · ${formatAgo(row.position.lastSeenAt)}` : ''}
        </span>
        {busy && row.freeAt ? <span className="ml-auto shrink-0">свободен {formatWhen(row.freeAt)}</span> : null}
      </footer>

      {serious.length > 0 ? (
        <ul className="space-y-1 border-t border-warn/30 bg-warn-soft px-4 py-2 text-xs text-warn">
          {serious.slice(0, 2).map((a) => (
            <li key={a}>{a}</li>
          ))}
        </ul>
      ) : null}
    </article>
  );
}
